/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useRef, useEffect, useState } from 'react';

import { DateRangeInput } from '../../generated/graphql';
import { TableFilterDateRangeField } from '../Inputs';

interface DateRange {
  checkInDate?: DateRangeInput | null;
  checkOutDate?: DateRangeInput | null;
}

interface DateRangeFilterChipProps {
  value: DateRange;
  onUpdate: (values: DateRange) => void;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}

const DateRangeFilterChip: FC<DateRangeFilterChipProps> = ({
  value,
  onUpdate,
  isEditing,
  onEditingChange,
}) => {
  const [localValue, setLocalValue] = useState<DateRange>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset local value when parent value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onEditingChange(false);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, onEditingChange]);

  const handleUpdate = () => {
    onUpdate(localValue);
    onEditingChange(false);
  };

  const formatDate = (date?: DateRangeInput | null) => {
    if (!date?.start) return 'N/A';
    return date.start;
  };

  const displayValue = `${formatDate(localValue.checkInDate)} → ${formatDate(localValue.checkOutDate)}`;

  const convertToInputFormat = (date?: DateRangeInput | null) => ({
    start: date?.start || '',
    end: date?.end || '',
  });

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => onEditingChange(!isEditing)}
        className="inline-flex items-center gap-x-1 rounded-xl border border-gray-200 bg-lightGreen px-3 py-1 text-sm hover:bg-lightGreen"
      >
        <span className="font-medium">Date Range:</span>
        <span>{displayValue}</span>
      </button>

      {isEditing && (
        <div className="absolute left-0 top-full z-10 mt-2 w-72 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-y-4">
            <TableFilterDateRangeField
              onChange={() => {}}
              meta={{ touched: false, error: undefined }}
              input={{
                name: 'checkInDate',
                value: convertToInputFormat(localValue.checkInDate),
                onChange: (value: any) => {
                  setLocalValue((prev) => ({
                    ...prev,
                    checkInDate: value,
                  }));
                },
                onBlur: () => {},
                onFocus: () => {},
              }}
              labelText="Check-in date"
              autoComplete="off"
            />
            <TableFilterDateRangeField
              onChange={() => {}}
              meta={{ touched: false, error: undefined }}
              input={{
                name: 'checkOutDate',
                value: convertToInputFormat(localValue.checkOutDate),
                onChange: (value: any) => {
                  setLocalValue((prev) => ({
                    ...prev,
                    checkOutDate: value,
                  }));
                },
                onBlur: () => {},
                onFocus: () => {},
              }}
              labelText="Check-out date"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={handleUpdate}
              className="rounded-md bg-secondaryBlue px-4 py-2 text-sm font-medium text-white hover:bg-secondaryBlueShade"
            >
              Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilterChip;
